import {forwardRef} from 'react'
import Svg, {Circle, G, Path, Polygon} from 'react-native-svg'

import {type Props, useCommonSVGProps} from './common'

/**
 * Small Lucide grape mark for icon slots.
 */
export const Mark = forwardRef(function MarkImpl(props: Props, ref) {
  const {fill, size, style, gradient, ...rest} = useCommonSVGProps(props)

  return (
    <Svg
      fill="none"
      {...rest}
      // @ts-ignore
      ref={ref}
      viewBox="0 0 24 24"
      width={size}
      height={size}
      style={[style]}
      stroke={fill}
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round">
      {gradient}
      <Path d="M22 5V2l-5.89 5.89" />
      <Circle cx="16.6" cy="15.89" r="3" />
      <Circle cx="8.11" cy="7.4" r="3" />
      <Circle cx="12.35" cy="11.65" r="3" />
      <Circle cx="13.91" cy="5.85" r="3" />
      <Circle cx="18.15" cy="10.09" r="3" />
      <Circle cx="6.56" cy="13.2" r="3" />
      <Circle cx="10.8" cy="17.44" r="3" />
      <Circle cx="5" cy="19" r="3" />
    </Svg>
  )
})

export function Full(
  props: Omit<Props, 'fill' | 'size' | 'height'> & {
    markFill?: Props['fill']
    textFill?: Props['fill']
  },
) {
  const {fill, size, style, gradient, ...rest} = useCommonSVGProps(props)
  const markSize = 360
  const gap = 80
  const textWidth = 2412
  const textHeight = 210
  const canvasHeight = 400
  const width = markSize + gap + textWidth
  const ratio = textHeight / width
  const markFill = props.markFill ?? '#C22EFF'
  const textFill = props.textFill ?? fill
  const markOffsetY = (canvasHeight - markSize) / 2
  const textOffsetY = (canvasHeight - textHeight) / 2
  const markScale = markSize / 24

  return (
    <Svg
      fill="none"
      {...rest}
      viewBox={`0 0 ${width} ${canvasHeight}`}
      width={size}
      height={size * ratio}
      style={[style]}>
      {gradient}
      <G
        transform={`translate(0 ${markOffsetY}) scale(${markScale})`}
        stroke={markFill}
        strokeWidth={2 / markScale}
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="none">
        <Path d="M22 5V2l-5.89 5.89" />
        <Circle cx="16.6" cy="15.89" r="3" />
        <Circle cx="8.11" cy="7.4" r="3" />
        <Circle cx="12.35" cy="11.65" r="3" />
        <Circle cx="13.91" cy="5.85" r="3" />
        <Circle cx="18.15" cy="10.09" r="3" />
        <Circle cx="6.56" cy="13.2" r="3" />
        <Circle cx="10.8" cy="17.44" r="3" />
        <Circle cx="5" cy="19" r="3" />
      </G>
      <G
        transform={`translate(${markSize + gap} ${textOffsetY})`}
        fill={textFill}
        fillRule="nonzero">
        <Path d="M120.15 209.862C93.628 209.862 71.467 205.9015 53.667 197.9805 35.867 190.0595 22.4725 178.3115 13.4835 162.7365 4.4945 147.1615 0 127.893 0 104.931V104.931C0 81.791 4.5835 62.4335 13.7505 46.8585 22.9175 31.2835 36.579 19.58 54.735 11.748 72.891 3.916 95.586 0 122.82 0H145.515C172.037 0 193.9755 3.0705 211.3305 9.2115 228.6855 15.3525 241.5015 23.9855 249.7785 35.1105 258.0555 46.2355 262.283 59.63 262.461 75.294V75.294H206.391V75.294C205.145 66.572 199.182 59.6745 188.502 54.6015 177.822 49.5285 162.781 46.992 143.379 46.992H126.291C110.093 46.992 97.01 48.8165 87.042 52.4655 77.074 56.1145 69.687 62.1665 64.881 70.6215 60.075 79.0765 57.672 90.513 57.672 104.931V104.931C57.672 119.171 60.164 130.474 65.148 138.84 70.132 147.206 77.875 153.258 88.377 156.996 98.879 160.734 112.763 162.603 130.029 162.603H146.583C162.603 162.603 175.7305 161.179 185.9655 158.331 196.2005 155.483 203.7655 151.2555 208.6605 145.6485 213.5555 140.0415 216.003 133.144 216.003 124.956V124.956L234.426 133.233H139.908V93.984H265.398V205.056H224.814V159.933L230.688 172.215C223.212 183.785 212.3095 192.952 197.9805 199.716 183.6515 206.48 165.095 209.862 142.311 209.862H120.15Z" />
        <Path d="M287.766 4.806H463.452C478.76 4.806 491.665 7.031 502.167 11.481S520.635 22.473 526.064 31.106 534.207 50.285 534.207 62.745V62.745C534.207 71.862 532.828 79.615 530.069 86.003S523.171 97.5 517.653 101.326 505.104 107.985 496.56 109.82V112.791C504.926 113.781 511.858 116.374 517.355 120.571S526.946 130.375 529.637 137.392 533.673 152.635 533.673 162.069V162.069C533.673 169.901 533.896 176.799 534.341 182.762S535.631 196.156 536.877 205.056H483.198C482.138 201.318 481.43 196.334 481.074 190.104S480.54 175.241 480.54 164.205V164.205C480.54 156.634 479.683 150.777 477.968 146.633S473.48 139.665 469.648 138.16 460.546 135.903 453.84 135.903H335.559V93.45H450.369C456.752 93.45 461.864 92.694 465.703 91.181S472.308 87.233 473.999 83.878 476.535 75.91 476.535 70.042V70.042C476.535 64.465 475.649 60.028 473.876 56.729S469.279 51.028 465.405 49.52 456.364 47.259 449.906 47.259H341.433V205.056H287.766V4.806Z" />
        <Path d="M625.46 131.097H747.479L761.363 172.749H610.775L625.46 131.097ZM655.631 4.806H718.643L828.38 205.056H768.038L688.739 49.395H685.001L604.1 205.056H545.36L655.631 4.806Z" />
        <Path d="M841.137 4.806H1011.483C1025.868 4.806 1038.319 7.521 1048.838 12.95S1067.42 26.122 1073.027 36.179 1081.437 58.206 1081.437 72.09V72.09C1081.437 85.974 1078.634 97.945 1073.027 108.002S1059.369 125.802 1048.876 131.231 1025.961 139.374 1011.613 139.374H872.376V96.921H990.924C999.29 96.921 1005.784 96.159 1010.405 94.635S1018.319 90.555 1020.284 86.967 1023.231 78.473 1023.231 72.249V72.249C1023.231 65.735 1022.249 60.685 1020.284 57.098S1015.026 50.975 1010.405 49.489 999.29 47.259 990.924 47.259H894.804V205.056H841.137V4.806Z" />
        <Polygon points="1100.066 4.806 1324.079 4.806 1324.079 51.798 1153.733 51.798 1153.733 83.571 1308.86 83.571 1308.86 126.291 1153.733 126.291 1153.733 158.064 1328.084 158.064 1328.084 205.056 1100.066 205.056" />
        <G transform="translate(1335.767 4.806)">
          <Polygon points="0 0 60.342 0 139.908 149.787 143.913 149.787 224.013 0 283.02 0 173.817 200.25 108.135 200.25" />
          <Polygon points="400.975 123.087 291.238 0 359.857 0 427.141 79.032 431.146 79.032 498.163 0 566.248 0 456.511 123.087 456.511 200.25 400.975 200.25" />
          <Polygon points="578.739 0 644.421 0 768.042 137.505 772.597 137.505 772.597 0 825.981 0 825.981 200.25 759.765 200.25 636.477 61.677 632.406 61.677 632.406 200.25 578.739 200.25" />
          <Polygon points="851.552 0 1075.565 0 1075.565 46.992 905.219 46.992 905.219 78.765 1060.346 78.765 1060.346 121.485 905.219 121.485 905.219 153.258 1079.57 153.258 1079.57 200.25 851.552 200.25" />
        </G>
      </G>
    </Svg>
  )
}
